/**
 * Cortex Initialization Animation
 * 
 * Stunning brain animation shown when Cortex AI agent initializes.
 * Provides visual feedback during component mounting and prevents premature errors.
 * 
 * @version 1.0.0
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@flowstate/utils';
import { Brain, Sparkles, Zap, Loader2 } from 'lucide-react';
import { agentBranding } from '../config/agentBranding';

interface CortexInitAnimationProps {
  isVisible?: boolean;
  onComplete?: () => void;
  isLoading?: boolean;
  loadingSteps?: string[];
  className?: string;
  /** 'bubble' = brain only, fills circle. 'bubble-outer' = rings/sparkles/zaps only, centered (outside bubble). */
  variant?: 'full' | 'bubble' | 'bubble-outer';
}

export const CortexInitAnimation: React.FC<CortexInitAnimationProps> = ({
  isVisible = true,
  onComplete,
  isLoading = false,
  loadingSteps = ['Loading conversations...', 'Initializing AI...', 'Ready'],
  className,
  variant = 'full'
}) => {
  const isBubble = variant === 'bubble';
  const isBubbleOuter = variant === 'bubble-outer';
  const [_currentStep, setCurrentStep] = useState(0);
  const [loadingText, setLoadingText] = useState(loadingSteps[0]);

  // Cycle through loading steps
  useEffect(() => {
    if (!isBubble && isVisible && isLoading) {
      const interval = setInterval(() => {
        setCurrentStep(prev => {
          const next = (prev + 1) % loadingSteps.length;
          setLoadingText(loadingSteps[next]);
          return next;
        });
      }, 800);
      return () => clearInterval(interval);
    }
    return;
  }, [isVisible, isLoading, loadingSteps, isBubble]);

  // Auto-complete after minimum duration (full variant only)
  useEffect(() => {
    if (!isBubble && isVisible && onComplete) {
      const timer = setTimeout(() => onComplete(), 2000);
      return () => clearTimeout(timer);
    }
    return;
  }, [isVisible, onComplete, isBubble]);

  if (isBubbleOuter) {
    return (
      <div className={cn('absolute inset-0 flex items-center justify-center pointer-events-none', className)} aria-hidden>
        {/* Centered on same point as brain/bubble (flex center = brain center); transformOrigin keeps rings scaled from center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            {[60, 75, 90].map((pct, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full border-2 border-primary/30"
                style={{
                  width: `${pct}%`,
                  height: `${pct}%`,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  transformOrigin: 'center center',
                }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: [0.8, 1.2, 0.8], opacity: [0, 0.6, 0] }}
                transition={{ duration: 2, delay: i * 0.2, repeat: Infinity, ease: 'easeInOut' }}
              />
            ))}
            {/* Sparkles: orbit brain center */}
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={`sparkle-${i}`}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ transformOrigin: 'center center' }}
                animate={{
                  rotate: [0, 360],
                  x: [0, Math.cos((i * Math.PI) / 2) * 35],
                  y: [0, Math.sin((i * Math.PI) / 2) * 35],
                }}
                transition={{ duration: 3, delay: i * 0.2, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="w-3 h-3 text-accent block -translate-x-1/2 -translate-y-1/2" />
              </motion.div>
            ))}
            {/* Zaps: either side of brain center */}
            {[0, 1].map((i) => (
              <motion.div
                key={`zap-${i}`}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ marginLeft: i === 0 ? '-30%' : '30%', marginTop: '-2%', transformOrigin: 'center center' }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: [0, 1, 0], scale: [0, 1.2, 0], rotate: [0, 180, 360] }}
                transition={{ duration: 1.5, delay: i * 0.75, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Zap className="w-3 h-3 text-primary fill-primary" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isBubble) {
    return (
      <motion.div
        className={cn('flex items-center justify-center w-full h-full rounded-full overflow-hidden', className)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Brain only, 50% size, centered in bubble */}
        <div className="w-full h-full rounded-full bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/40 flex items-center justify-center shadow-lg shadow-primary/30">
          <Brain className="w-[50%] h-[50%] text-primary" strokeWidth={1.5} />
        </div>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait" onExitComplete={onComplete}>
      {isVisible && (
        <motion.div
          className={cn(
            'flex flex-col items-center justify-center w-full h-full',
            'bg-gradient-to-br from-background via-background/95 to-primary/5',
            className
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="relative flex flex-col items-center justify-center space-y-6 p-8">
            {/* Brain Icon with Pulsing Effect */}
            <motion.div
              className="relative"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ 
                duration: 0.5,
                ease: [0.34, 1.56, 0.64, 1] // Spring-like easing
              }}
            >
              {/* Outer Glow Rings */}
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0 rounded-full border-2 border-primary/30"
                  style={{
                    width: 120 + i * 30,
                    height: 120 + i * 30,
                    left: -(i * 15),
                    top: -(i * 15),
                  }}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{
                    scale: [0.8, 1.2, 0.8],
                    opacity: [0, 0.6, 0],
                  }}
                  transition={{
                    duration: 2,
                    delay: i * 0.2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              ))}

              {/* Main Brain Icon Container */}
              <motion.div
                className="relative z-10 w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 backdrop-blur-md border border-primary/40 flex items-center justify-center shadow-2xl shadow-primary/50"
                animate={{
                  boxShadow: [
                    '0 0 20px hsl(var(--primary) / 0.3)',
                    '0 0 60px hsl(var(--primary) / 0.6)',
                    '0 0 20px hsl(var(--primary) / 0.3)',
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <Brain className="w-12 h-12 text-primary" strokeWidth={1.5} />
              </motion.div>

              {/* Orbiting Sparkles */}
              {[0, 1, 2, 3].map((i) => (
                <motion.div
                  key={`sparkle-${i}`}
                  className="absolute"
                  style={{
                    left: '50%',
                    top: '50%',
                  }}
                  animate={{
                    rotate: [0, 360],
                    x: [0, Math.cos((i * Math.PI) / 2) * 80],
                    y: [0, Math.sin((i * Math.PI) / 2) * 80],
                  }}
                  transition={{
                    duration: 3,
                    delay: i * 0.2,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                >
                  <Sparkles className="w-4 h-4 text-accent" />
                </motion.div>
              ))}

              {/* Energy Zaps */}
              {[0, 1].map((i) => (
                <motion.div
                  key={`zap-${i}`}
                  className="absolute"
                  style={{
                    left: i === 0 ? '10%' : '80%',
                    top: '50%',
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0, 1.2, 0],
                    rotate: [0, 180, 360],
                  }}
                  transition={{
                    duration: 1.5,
                    delay: i * 0.75,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                >
                  <Zap className="w-6 h-6 text-primary fill-primary" />
                </motion.div>
              ))}
            </motion.div>

            {/* Cortex Text with Typing Effect */}
            <motion.div
              className="flex flex-col items-center space-y-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <motion.h1
                className="text-3xl font-bold tracking-wider"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent bg-[length:200%_100%] animate-gradient">
                  {agentBranding.fullName}
                </span>
              </motion.h1>
              
              <motion.p
                className="text-sm text-muted-foreground font-medium tracking-wide flex items-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0.7, 1] }}
                transition={{ 
                  delay: 0.8,
                  duration: 1,
                  times: [0, 0.5, 0.8, 1]
                }}
              >
                {isLoading && (
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                )}
                {loadingText}
              </motion.p>
            </motion.div>

            {/* Loading Progress Bar */}
            <motion.div
              className="w-48 h-1 bg-primary/10 rounded-full overflow-hidden"
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ delay: 0.6, duration: 0.4 }}
            >
              <motion.div
                className="h-full bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%]"
                initial={{ x: '-100%' }}
                animate={{ 
                  x: '100%',
                  backgroundPosition: ['0% 0%', '200% 0%']
                }}
                transition={{
                  x: {
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  },
                  backgroundPosition: {
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                  }
                }}
              />
            </motion.div>

            {/* Neural Network Grid Background */}
            <motion.div
              className="absolute inset-0 -z-10 opacity-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.05 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="cortex-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="1" fill="currentColor" className="text-primary" />
                    <line x1="20" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="0.5" className="text-primary" />
                    <line x1="20" y1="20" x2="20" y2="40" stroke="currentColor" strokeWidth="0.5" className="text-primary" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#cortex-grid)" />
              </svg>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CortexInitAnimation;
